/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';
import SelectRowCell from 'psa-backgrid/SelectRowCell';

import GridView from '@/views/GridView';
import PageableCollection from '@/backbone/PageableCollection';
import MultiButtonCell from '@/backgrid/MultiButtonCell';
import showModal from '@/util/showModal';
import ConfirmationModalView from '@/views/ConfirmationModalView';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';

import AWPackageRemarkModalView from '../AWPackageRemarkModalView';
import UpdateAWPackageRemarks from '../../services/UpdateAWPackageRemarks';

import tmplAWPackageRemarksView from './AWPackageRemarksView.hbs';

const AWPackageRemarksView = View.extend({
  template: tmplAWPackageRemarksView,

  ui: {
    remarksGrid: '.remarks-grid',
    btnAddRemark: '.btn-add-remark'
  },

  regions: {
    remarksGrid: '@ui.remarksGrid'
  },

  events: {
    'click @ui.btnAddRemark': 'onClickAddRemark'
  },

  onRender() {
    const isEditable = this.model.get('editable');
    const allRemarks = this.model.get('remarks');
    this.remarksCollection = new PageableCollection(allRemarks);

    this.remarksGrid = new GridView({
      columns: [
        {
          name: 'selected',
          label: '',
          cell: SelectRowCell.SelectRowCell,
          headerCell: SelectRowCell.SelectAllHeaderCell,
          width: 10,
          editable: isEditable,
          sortable: false
        },
        {
          name: 'description',
          label: 'Description',
          cell: 'string',
          headerCell: 'custom',
          width: 25,
          editable: false,
          sortable: false
        },
        {
          name: 'remark',
          label: 'Remark',
          cell: 'string',
          headerCell: 'custom',
          width: 45,
          editable: false,
          sortable: false
        },
        {
          name: '',
          label: '',
          cell: MultiButtonCell.extend({
            buttons: [
              {
                name: 'Edit',
                buttonClass: 'btn-secondary btn-sm',
                onClick: this.onClickEditRemark.bind(this)
              },
              {
                name: 'Remove',
                buttonClass: 'btn-danger btn-sm ladda-button',
                onClick: this.onClickDeleteRemark.bind(this)
              }
            ]
          }),
          headerCell: 'custom',
          width: 20,
          editable: false,
          renderable: isEditable
        }
      ],
      collection: this.remarksCollection,
      emptyText: 'No Remarks Found'
    });

    this.showChildView('remarksGrid', this.remarksGrid);

    const selectedRemarks = this.getSelectedRemarks(allRemarks);
    const selectedRemarkIds = this.getRemarkIds(selectedRemarks);
    _.each(selectedRemarkIds, (srId) => {
      const selectedModel = _.find(this.remarksGrid.collection.fullCollection.models, (remarkModel) => {
        return remarkModel.get('id') === srId;
      });
      selectedModel.trigger('backgrid:select', selectedModel, true);
    });
  },

  getSelectedRemarks(remarks) {
    return _.filter(remarks, (remark) => remark.isSelected === '1');
  },

  getRemarkIds(remarks) {
    return _.map(remarks, (remark) => remark.id);
  },

  async onClickAddRemark(e) {
    e.preventDefault();
    const self = this;
    await showModal({
      title: 'Add Remark',
      view: new AWPackageRemarkModalView(),
      dialogClasses: 'modal-lg'
    }).then(
      this.listenTo(Radio.channel('remarks'), 'add:remark', (newRemark) => {
        self.remarksCollection.fullCollection.add(newRemark);
        Radio.channel('remarks').off('add:remark');
      })
    );
  },

  async onClickEditRemark(e, remark) {
    e.preventDefault();
    await showModal({
      title: 'Edit Remark',
      view: new AWPackageRemarkModalView({
        model: remark,
        editRemark: true
      }),
      dialogClasses: 'modal-lg'
    }).then(
      this.listenTo(Radio.channel('remarks'), 'edit:remark', (updatedRemark) => {
        remark.set(updatedRemark);
        Radio.channel('remarks').off('edit:remark');
      })
    );
  },

  async onClickDeleteRemark(e, remark) {
    e.preventDefault();
    const self = this;
    const message = `Are you sure you want to remove this remark?`;
    showModal({
      title: 'Edit Remark',
      view: new ConfirmationModalView({
        confirmText: 'Remove',
        model: new Backbone.Model({
          message,
          onConfirm() {
            return Promise.resolve();
          },
          onConfirmDone() {
            self.remarksCollection.remove(remark);
          }
        })
      })
    });
  },

  async onSave() {
    if (!this.model.get('editable')) {
      return;
    }

    const selectedModels = this.remarksGrid.viewGrid.getSelectedModels();
    _.each(selectedModels, (model) => {
      model.set({ isSelected: '1' });
    });

    const unSelectedModels = _.filter(this.remarksCollection.fullCollection.models, (model) => {
      return !_.includes(selectedModels, model);
    });
    _.each(unSelectedModels, (model) => {
      model.set({ isSelected: '0' });
    });

    const formData = {};
    formData.remarks = this.remarksCollection.fullCollection.toJSON();
    formData.awPackageId = this.model.get('awPackageId');

    try {
      await UpdateAWPackageRemarks(formData);
      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'AW Package Information Saved.',
        hideAfter: 1
      });
    } catch (err) {
      showErrorMessage({
        errorCode: 'saveError',
        errorTitle: 'Save Error',
        errorMessage: 'Error Saving File.'
      });
      throw new Error(err);
    }
  }
});

export default AWPackageRemarksView;
